import React, { useState } from 'react';

/**
 * Enregistrer la configuration courante depuis l'écran Coter.
 *
 * Le chargement d'une configuration existait déjà - c'est le sélecteur de
 * profil en haut de l'écran, qui recharge les neuf paramètres. Ce qui manquait,
 * c'était le chemin inverse : après avoir ajusté un taux ou un plafond pour
 * une cotation, rien ne permettait de conserver ce réglage sans passer par
 * Réglages et le ressaisir à la main. On le refaisait donc à chaque fois.
 *
 * Les paramètres restent volontairement dissociés du profil tant qu'on ne
 * demande rien : « une cotation ponctuelle ne doit pas réécrire un profil
 * réutilisable » (document 04). L'enregistrement est un geste explicite.
 */
export default function ActionsProfil({ profil, modifie, onMettreAJour, onCreer, onRetablir }) {
  const [nom, setNom] = useState(null);   // null = formulaire fermé
  const [occupe, setOccupe] = useState(false);

  const creer = async () => {
    const propre = nom.trim();
    if (!propre || occupe) return;
    setOccupe(true);
    try { await onCreer(propre); setNom(null); } finally { setOccupe(false); }
  };

  const bouton = (variante) => ({
    padding: 'var(--espace-2) var(--espace-3)',
    border: `1px solid ${variante === 'plein' ? 'var(--couleur-accent)' : 'var(--couleur-trait-fort)'}`,
    borderRadius: 'var(--rayon)',
    background: variante === 'plein' ? 'var(--couleur-accent)' : 'transparent',
    color: variante === 'plein' ? 'var(--couleur-sur-accent)' : 'var(--couleur-encre)',
    fontWeight: variante === 'plein' ? 600 : 400,
    fontSize: '0.85rem',
  });

  return (
    <div style={{ borderTop: '1px solid var(--couleur-trait)', paddingTop: 'var(--espace-3)' }}>
      <div className="etiquette" style={{ marginBottom: 'var(--espace-2)' }}>Profil</div>

      <p style={{ margin: '0 0 var(--espace-3)', fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
        {modifie
          ? <>Ces paramètres diffèrent du profil <strong>{profil.name}</strong>. Ils ne s’appliquent qu’à la cotation en cours.</>
          : <>Ces paramètres sont ceux du profil <strong>{profil.name}</strong>.</>}
      </p>

      <div style={{ display: 'flex', gap: 'var(--espace-2)', flexWrap: 'wrap' }}>
        {modifie && (
          <>
            <button type="button" style={bouton('plein')} onClick={onMettreAJour}>
              Mettre à jour « {profil.name} »
            </button>
            <button type="button" style={bouton()} onClick={onRetablir}>
              Rétablir
            </button>
          </>
        )}
        {nom === null && (
          <button type="button" style={bouton()} onClick={() => setNom('')}>
            Enregistrer comme nouveau profil
          </button>
        )}
      </div>

      {/* Formulaire en ligne plutôt que `window.prompt` : la boîte native est
          bloquée dans une application installée en mode autonome, et le geste
          y serait sans issue. */}
      {nom !== null && (
        <div style={{ display: 'flex', gap: 'var(--espace-2)', marginTop: 'var(--espace-3)', flexWrap: 'wrap' }}>
          <input
            type="text"
            autoFocus
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') creer(); if (e.key === 'Escape') setNom(null); }}
            placeholder={`${profil.name} (ajusté)`}
            aria-label="Nom du nouveau profil"
            className="champ"
            style={{ flex: 1, minWidth: 180, fontFamily: 'var(--police-texte)' }}
          />
          <button type="button" style={bouton('plein')} onClick={creer} disabled={!nom.trim() || occupe}>
            Créer
          </button>
          <button type="button" style={bouton()} onClick={() => setNom(null)}>
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}
